import { AxiosResponse } from 'axios';
import { WEATHER_PAGE_SIZE } from 'ots-constants';
import { useMustacheTranslation, useOTSTranslation } from 'i18n/useOTSTranslation';
import { SidebarAd } from 'googlead/components/SidebarAd';
import {
  fetchRegionFeatured,
  fetchRegionMeta,
  fetchRegionOpeningDate, fetchRegionRelated, fetchRegionRelatedNews,
  fetchRegionResortsByStatus,
} from 'api/region/static';
import { IRegionResortsPaginated, IRegionShort } from 'api/region/types';
import { CurrentLink, CurrentTab } from 'components/region/RegionHeaderNav/constants';
import { BoxWithShadow } from 'components/blocks/BoxWithShadow';
import { HeaderNavCurLink } from 'components/common/HeaderNav';
import { BlockContent } from 'components/blocks/BlockContent';
import { BlockHeader } from 'components/blocks/BlockHeader';
import { Main } from 'components/resort/Layout/Main';
import { Side } from 'components/resort/Layout/Side';
import { useRegionLayoutBubble } from 'page-components/region/_context';
import { IRegionLayout } from 'page-components/region/_layout';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import { getRegionSlug } from 'util/memcache';
import { IRelatedNews, ResortStatus } from 'api/resort/types';
import PlanYourTrip from 'components/common/PlanYourTrip/RegionWidget';

import { OpenDatesTable } from 'components/region/OpenDatesTable';
import { Hero400Error } from 'components/400';
import { formatDateFromStr, MINIMAL_DATE_FORMAT } from 'util/formatDate';
import SurfaceRelatedArticles from 'components/resort/SurfaceRelatedArticles';
import React from 'react';
import { assertShouldBeNot404, wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';

export type RegionProjectedOpeningPageProps = {
  resorts: IRegionResortsPaginated,
  pathInfo: IRegionShort,
  openingDate: string,
  numResorts: number,
  relatedNews: IRelatedNews | null,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-projected-openings-page';
export const TEMPLATE_ID = '2.1.5';
export const TEMPLATE_NAME = 'Region Projected Opening';
const PAGE_RESORTS_STATUS = ResortStatus.openingDates;

export default function RegionProjectedOpeningsPage({
  resorts, numResorts, openingDate, pathInfo,
  relatedNews,
}: RegionProjectedOpeningPageProps) {
  useRegionLayoutBubble({});
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');

  if (!resorts) {
    return <Hero400Error light />;
  }

  const seoTitle = pathInfo.isLogical ? t('region.openDates.logicSeoTitle', {
    region: pathInfo.title,
  }) : t('region.openDates.seoTitle', {
    region: pathInfo.title,
  });

  const seoText = pathInfo.isLogical ? m('region.openDates.logicSeoText', {
    region: pathInfo.title,
    numresorts: numResorts,
    firstopendate: formatDateFromStr(openingDate, MINIMAL_DATE_FORMAT),
  })
    : m('region.openDates.seoText', {
      region: pathInfo.title,
      numresorts: numResorts,
      firstopendate: formatDateFromStr(openingDate, MINIMAL_DATE_FORMAT),
    });

  return (
    <>
      <Main>
        <BoxWithShadow>
          <BlockHeader title={seoTitle} />
          <BlockContent>
            {seoText}
          </BlockContent>
        </BoxWithShadow>
        {!!resorts.pagination?.count && (
          <OpenDatesTable
            regionId={pathInfo.uuid}
            list={resorts}
            hasMore={resorts.pagination?.count > resorts.pagination?.limit}
          />
        )}
        <PlanYourTrip
          region={pathInfo}
          hasSkiRental={false}
          hasCarRental={false}
          hasFlights={false}
          isPartner={false}
        />
      </Main>
      <Side>
        <SurfaceRelatedArticles
          relatedNewsList={relatedNews}
          isFresh
        />
        <SidebarAd isRegion id={pathInfo.uuid} showWeSki={false} />
      </Side>
    </>
  );
}

export const fetchProps = async ({ region }: { region: string }): Promise<RegionProjectedOpeningPageProps> => {
  const cache: AxiosResponse<IRegionShort> = await assertShouldBeNot404(() => getRegionSlug(region));
  const { uuid } = cache.data;
  const featured = await fetchRegionFeatured(uuid);
  const resorts = await fetchRegionResortsByStatus(uuid, PAGE_RESORTS_STATUS, 0, WEATHER_PAGE_SIZE);

  return {
    type: 'region',
    route: 'projected-openings',
    resorts,
    featured,
    pathInfo: cache.data,
    numResorts: (await fetchRegionMeta(uuid))?.resorts?.active,
    openingDate: await fetchRegionOpeningDate(uuid),
    relatedNews: await fetchRegionRelatedNews(uuid),

    /**
       * Layout
       */
    related: await fetchRegionRelated(uuid),
    navigation: {
      head: HeaderNavCurLink.SNOW_REPORTS,
      tab: CurrentTab.TRIP_PLANNING,
      link: CurrentLink.PROJECTED_OPENINGS,
    },
    adTKey: 'metaregion projected-openings',
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    hero: {
      title: 'region.openDates.heroTitle',
    },
    meta: {
      redirectTo: cache.data.domain ?? null,
      description: 'MetaRegion.projected_openings.description',
      title: 'MetaRegion.projected_openings.title',
    },
    prefetch: false,
  };
};

// This function gets called at build time
// eslint-disable-next-line max-len
export const getRegionProjectedOpeningsPageStaticProps = wrapStaticProps(fetchProps);

export const getRegionProjectedOpeningsPageServerSideProps = wrapServerSideProps(fetchProps);
